import React, { Component } from "react";
import { Modal, Select, Input, Button, Divider, Alert, Space, Typography } from "antd";
import firebase from "firebase";
import {
  getImpersonatedUser,
  setImpersonatedUser,
  clearImpersonatedUser,
} from "../js/impersonation";

const { Text } = Typography;

/**
 * Admin-only modal for "View as User".
 *
 * Sources for picking a target user, in order of preference:
 *   1. The currently selected distributor's team roster (already loaded in
 *      `props.DistributorTeam` as `[{ value: uid, label: email }]`).
 *   2. A search across every distributor the admin has access to (props.Distributors).
 *   3. A manual UID entry (with optional email for display).
 */
export default class ImpersonateUserModal extends Component {
  state = {
    selectedUid: null,
    selectedEmail: null,
    manualUid: "",
    manualEmail: "",
    emailSearch: "",
    crossTeamRoster: null, // [{ uid, email, distributorKey, distributorName }]
    crossTeamLoading: false,
    crossTeamError: null,
  };

  loadCrossTeamRosterIfNeeded = () => {
    if (this.state.crossTeamRoster || this.state.crossTeamLoading) return;
    const distributors = this.props.Distributors;
    if (!distributors) return;

    this.setState({ crossTeamLoading: true, crossTeamError: null });

    const distKeys = Object.keys(distributors);
    Promise.all(
      distKeys.map((key) =>
        firebase
          .database()
          .ref(`/d/${key}/Team`)
          .once("value")
          .then((snapshot) => ({ key, info: distributors[key], team: snapshot.val() || {} }))
          .catch(() => ({ key, info: distributors[key], team: {} })),
      ),
    )
      .then((results) => {
        const roster = [];
        results.forEach(({ key, info, team }) => {
          Object.entries(team).forEach(([uid, member]) => {
            if (!member || member.active === false) return;
            roster.push({
              uid,
              email: member.email || "",
              distributorKey: key,
              distributorName: (info && info.name) || key,
            });
          });
        });
        this.setState({ crossTeamRoster: roster, crossTeamLoading: false });
      })
      .catch((err) => {
        this.setState({
          crossTeamLoading: false,
          crossTeamError: err && err.message ? err.message : "Failed to load teams",
        });
      });
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.loadCrossTeamRosterIfNeeded();
    }
  }

  pickFromCurrentTeam = (uid) => {
    const team = this.props.DistributorTeam || [];
    const found = team.find((t) => t.value === uid);
    this.setState({ selectedUid: uid, selectedEmail: found ? found.label : null });
  };

  pickFromCrossTeam = (uid) => {
    const roster = this.state.crossTeamRoster || [];
    const found = roster.find((r) => r.uid === uid);
    this.setState({ selectedUid: uid, selectedEmail: found ? found.email : null });
  };

  startImpersonation = (uid, email) => {
    if (!uid) return;
    setImpersonatedUser({ uid, email: email || null });
    window.location.reload();
  };

  stopImpersonation = () => {
    clearImpersonatedUser();
    window.location.reload();
  };

  render() {
    const { visible, onClose, DistributorTeam, DistributorInfo } = this.props;
    const current = getImpersonatedUser();
    const team = DistributorTeam || [];
    const teamLabel =
      DistributorInfo && DistributorInfo.name
        ? `Team for ${DistributorInfo.name}`
        : "Current team";

    const crossTeamRoster = this.state.crossTeamRoster || [];
    const search = this.state.emailSearch.trim().toLowerCase();
    const filteredCrossTeam = search
      ? crossTeamRoster.filter(
          (r) =>
            (r.email && r.email.toLowerCase().includes(search)) ||
            (r.uid && r.uid.toLowerCase().includes(search)),
        )
      : crossTeamRoster.slice(0, 50);

    return (
      <Modal
        title="View as User"
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={640}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {current ? (
            <Alert
              type="warning"
              showIcon
              message={
                <span>
                  Currently viewing as <Text strong>{current.email || current.uid}</Text>
                </span>
              }
              action={
                <Button danger size="small" onClick={this.stopImpersonation}>
                  Stop
                </Button>
              }
            />
          ) : (
            <Alert
              type="info"
              showIcon
              message="Pick a customer to view as. The page will reload and load their account state."
            />
          )}

          <div>
            <Text strong>{teamLabel}</Text>
            <Select
              showSearch
              placeholder={
                team.length
                  ? "Pick a teammate's email..."
                  : "No teammates loaded for the current team"
              }
              optionFilterProp="children"
              style={{ width: "100%", marginTop: 6 }}
              value={this.state.selectedUid || undefined}
              onChange={this.pickFromCurrentTeam}
              disabled={!team.length}
            >
              {team.map((t) => (
                <Select.Option key={t.value} value={t.value}>
                  {t.label} <Text type="secondary">({t.value})</Text>
                </Select.Option>
              ))}
            </Select>
          </div>

          <Divider style={{ margin: "8px 0" }}>OR — search across all teams you can access</Divider>

          <div>
            <Input
              placeholder="Search by email or UID across every team..."
              value={this.state.emailSearch}
              onChange={(e) => this.setState({ emailSearch: e.target.value })}
              allowClear
            />
            {this.state.crossTeamLoading && (
              <Text type="secondary" style={{ display: "block", marginTop: 6 }}>
                Loading rosters from {Object.keys(this.props.Distributors || {}).length} teams…
              </Text>
            )}
            {this.state.crossTeamError && (
              <Text type="danger" style={{ display: "block", marginTop: 6 }}>
                {this.state.crossTeamError}
              </Text>
            )}
            {!this.state.crossTeamLoading && !!crossTeamRoster.length && (
              <Select
                placeholder={`${crossTeamRoster.length} teammates available`}
                style={{ width: "100%", marginTop: 6 }}
                showSearch
                optionFilterProp="children"
                value={this.state.selectedUid || undefined}
                onChange={this.pickFromCrossTeam}
              >
                {filteredCrossTeam.map((r) => (
                  <Select.Option key={`${r.distributorKey}::${r.uid}`} value={r.uid}>
                    {r.email || "(no email)"}{" "}
                    <Text type="secondary">
                      — {r.distributorName} · {r.uid}
                    </Text>
                  </Select.Option>
                ))}
              </Select>
            )}
          </div>

          <Divider style={{ margin: "8px 0" }}>OR — enter a UID directly</Divider>

          <Space.Compact style={{ width: "100%" }}>
            <Input
              placeholder="Firebase Auth UID"
              value={this.state.manualUid}
              onChange={(e) => this.setState({ manualUid: e.target.value })}
            />
            <Input
              placeholder="Email (optional, for display)"
              value={this.state.manualEmail}
              onChange={(e) => this.setState({ manualEmail: e.target.value })}
            />
          </Space.Compact>

          <Space style={{ justifyContent: "flex-end", width: "100%" }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              disabled={!this.state.selectedUid && !this.state.manualUid.trim()}
              onClick={() => {
                if (this.state.manualUid.trim()) {
                  this.startImpersonation(
                    this.state.manualUid.trim(),
                    this.state.manualEmail.trim() || null,
                  );
                  return;
                }
                this.startImpersonation(this.state.selectedUid, this.state.selectedEmail);
              }}
            >
              View as this user
            </Button>
          </Space>

          <Text type="secondary" style={{ fontSize: 12 }}>
            You'll keep your own auth token; only the per-user state loaded into the UI
            (Distributors, preferences, drink type, saved searches) will reflect the
            target user. Writes you make while viewing will not be saved to the customer's
            account.
          </Text>
        </Space>
      </Modal>
    );
  }
}
